import { ref, Ref, watch, nextTick } from "vue";
import { getEnvVar } from "@/utils";
import {
  upliftPayment,
  useIsDesktop,
  UpliftOrder,
  HotelInfo,
  prepareHotelReservations,
  formatDate,
} from "vacv-common";
import { ProductExtended } from "@/../types/promos-types";

export default function useFlexPayMonthly(
  displayedProducts: Ref<ProductExtended[]>
) {
  const enableUplift = getEnvVar("VITE_ENABLE_UPLIFT") === "true";
  const SHOULD_SEND_SENTRY = getEnvVar("VITE_SENTRY_SHOULD_SEND") === "true";

  const { initPayMonthly, loadUpliftLib } = upliftPayment(
    enableUplift,
    SHOULD_SEND_SENTRY,
    [],
    getEnvVar("VITE_UPLIFT_LIB_UPC"),
    getEnvVar("VITE_UPLIFT_LIB_API_KEY")
  );

  const upliftInited = ref(false);

  if (enableUplift) {
    window.upReady = function () {
      if (!upliftInited.value) {
        const isDesktop = useIsDesktop();
        initPayMonthly(false, isDesktop.value);
        upliftInited.value = true;
        sendUpliftInfo(displayedProducts);
      }
    };
    loadUpliftLib();

    watch(displayedProducts, async () => {
      // use nextTick to make sure the displayProducts are rendered before calling sendUpliftInfo
      await nextTick();
      sendUpliftInfo(displayedProducts);
    });
  }

  return { upliftInited };
}

export function sendUpliftInfo(displayedProducts: Ref<ProductExtended[]>) {
  const orderInfo = prepareFlexpayOrderInfo(displayedProducts);
  if (orderInfo) {
    window.Uplift.Payments.load(orderInfo);
  }
}

export function prepareFlexpayOrderInfo(
  displayedProducts: Ref<ProductExtended[]>
): UpliftOrder | undefined {
  const defaultPromotion = displayedProducts.value[0];
  if (!defaultPromotion) {
    return;
  }
  const hotelInfo: HotelInfo = {
    numberOfRooms: 1,
    checkIn: formatDate(
      defaultPromotion.departure_date as string,
      "yyyy-MM-dd",
      "yyyyMMdd"
    ),
    checkOut: formatDate(
      defaultPromotion.return_date as string,
      "yyyy-MM-dd",
      "yyyyMMdd"
    ),
    roomName: "",
    hotelName: defaultPromotion?.hotel_name ?? "",
  };

  const promoPrice = defaultPromotion?.price ?? 0;
  const orderAmount =
    Math.round((promoPrice * 100 + Number.EPSILON) * 100) / 100;

  const orderInfo: UpliftOrder = {
    travelers: [],
    air_reservations: [],
    hotel_reservations: prepareHotelReservations(hotelInfo),
    add_ons: [],
    order_amount: orderAmount,
  };

  return orderInfo;
}
